/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPixels,
  numericValues,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { Skeleton } from "../../_core/Skeleton/Skeleton.emotion";
import { Text } from "../../_core/Text/Text.emotion";

const containerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.background,
  border: `2px solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: radius.m,
  display: "flex",
});

const interactiveStyle: Interpolation<ThemeType> = (theme) => ({
  appearance: "none",
  cursor: "pointer",
  font: "inherit",
  color: "inherit",
  width: "100%",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  ":hover": {
    backgroundColor: theme.semanticColors.neutralsVeryLow,
    borderColor: theme.semanticColors.neutralsVeryLow,
  },
});

const selectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.primary,
  borderColor: theme.semanticColors.primary,
  ":hover": {
    backgroundColor: theme.semanticColors.primary,
    borderColor: theme.semanticColors.primary,
  },
});

const selectedTextStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.background,
});

const containerVerticalStyle: Interpolation = {
  flexDirection: "column",
  gap: spacing.xxs,
  justifyContent: "center",
  paddingBlock: spacing.l,
  paddingInline: spacing.s,
  textAlign: "center",
};
const valueTextVerticalStyle: Interpolation<ThemeType> = (theme) => ({
  alignSelf: "stretch",
  fontFamily: theme.fontFamilies.sansSerif,
  fontSize: numericPixels[32],
  fontWeight: numericValues[400],
});
const labelTextVerticalStyle: Interpolation<ThemeType> = (theme) => ({
  alignSelf: "stretch",
  color: theme.semanticColors.neutralsHigh,
});
const skeletonVerticalStyle: Interpolation = {
  height: "106px",
};

const containerHorizontalStyle: Interpolation = {
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: spacing.ms,
  width: "100%",
};
const labelTextHorizontalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  textAlign: "left",
});
const valueTextHorizontalStyle: Interpolation = {
  marginLeft: spacing.xs,
  textAlign: "right",
};
const skeletonHorizontalStyle: Interpolation = {
  height: "42px",
};

export type StatisticCardProps = {
  value: string;
  label: string;
  loading?: boolean;
  className?: string;
  variant?: "vertical" | "horizontal";
  onClick?: () => void;
  selected?: boolean;
};

export const StatisticCard = ({
  value,
  label,
  loading,
  variant = "vertical",
  className = "",
  onClick,
  selected = false,
}: StatisticCardProps) => {
  if (loading) {
    return (
      <Skeleton
        css={
          variant === "vertical"
            ? skeletonVerticalStyle
            : skeletonHorizontalStyle
        }
      />
    );
  }

  const Element = onClick ? "button" : "div";
  const interactiveProps = onClick
    ? { type: "button" as const, onClick, "aria-pressed": selected }
    : {};
  const interactiveCSS = onClick
    ? [interactiveStyle, selected && selectedStyle]
    : [];

  if (variant === "horizontal") {
    return (
      <Element
        css={[containerStyle, containerHorizontalStyle, ...interactiveCSS]}
        className={className}
        {...interactiveProps}
      >
        <Text
          overflow="ellipsis"
          type="title"
          size="xs"
          css={[labelTextHorizontalStyle, selected && selectedTextStyle]}
        >
          {label}
        </Text>
        <Text
          type="detail"
          size="xl"
          css={[valueTextHorizontalStyle, selected && selectedTextStyle]}
        >
          {value}
        </Text>
      </Element>
    );
  }

  return (
    <Element
      css={[containerStyle, containerVerticalStyle, ...interactiveCSS]}
      className={className}
      {...interactiveProps}
    >
      <span css={[valueTextVerticalStyle, selected && selectedTextStyle]}>
        {value}
      </span>
      <Text
        overflow="ellipsis"
        type="detail"
        size="l"
        css={[labelTextVerticalStyle, selected && selectedTextStyle]}
      >
        {label}
      </Text>
    </Element>
  );
};

StatisticCard.displayName = "StatisticCard";
